import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronLeft, Loader2, Target, TrendingUp, Sparkles, Download,
  AlertTriangle, CheckCircle, AlertCircle, Zap, Mail, X, Plus,
  Eye, MailCheck, RefreshCw, BellRing,
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Legend, Tooltip as ReTooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_META = {
  critical: { label: 'Critical',     color: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/10', border: 'border-[#FF3B30]/30', Icon: AlertTriangle },
  warning:  { label: 'Warning',      color: 'text-[#FF9500]', bg: 'bg-[#FF9500]/10', border: 'border-[#FF9500]/30', Icon: AlertCircle },
  minor:    { label: 'Minor',        color: 'text-[#AF52DE]', bg: 'bg-[#AF52DE]/10', border: 'border-[#AF52DE]/30', Icon: Zap },
  on_par:   { label: 'On par / Ahead', color: 'text-[#34C759]', bg: 'bg-[#34C759]/10', border: 'border-[#34C759]/30', Icon: CheckCircle },
};

const KPI_LABELS = {
  time_to_competency_days_saved: { label: 'Time-to-competency', unit: 'days saved', good: 'positive' },
  productivity_uplift_pct:       { label: 'Productivity uplift', unit: 'pts',       good: 'positive' },
  rework_rate_reduction_pct:     { label: 'Rework reduction',   unit: 'pts',       good: 'positive' },
  innovation_rate_uplift:        { label: 'Innovation rate',    unit: 'init./yr',  good: 'positive' },
  onboarding_weeks_saved:        { label: 'Onboarding time',    unit: 'weeks saved', good: 'positive' },
  retention_pct_uplift:          { label: 'Retention uplift',   unit: 'pts',       good: 'positive' },
};

/**
 * KM Benchmarking — compares the caller's org (aggregated from their assessment
 * submissions) against curated industry peer norms. Shows radar, gap table,
 * percentile, projected KPI uplift, and GPT-5.2 insights.
 *
 * Data flow:
 *  1) user picks industry + company_size + scope
 *  2) POST /api/km-benchmarking/report → server computes gaps + KPI deltas
 *  3) (optional) POST /api/km-benchmarking/reports/{id}/ai-insights → GPT-5.2 analysis
 *  4) GET /api/km-benchmarking/reports/{id}/export.pdf → executive PDF
 */
const KMBenchmarkingPanel = ({ token, onBack }) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [options, setOptions] = useState({ industries: [], sizes: [] });
  const [industry, setIndustry] = useState('tech');
  const [size, setSize] = useState('51-250');
  const [scope, setScope] = useState('domain');
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  // Share-activity state (per-recipient open tracking)
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [nudging, setNudging] = useState(false);
  // Trajectory state (sparkline of org vs industry P50 over time)
  const [trajectory, setTrajectory] = useState(null);
  // Email-share dialog state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmails, setShareEmails] = useState([]);
  const [shareEmailInput, setShareEmailInput] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareIncludePdf, setShareIncludePdf] = useState(true);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/km-benchmarking/industries`, { headers });
        setOptions(r.data);
      } catch { /* silent */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runReport = async () => {
    setGenerating(true);
    setReport(null);
    try {
      const r = await axios.post(`${API}/km-benchmarking/report`, {
        industry, company_size: size, scope,
      }, { headers });
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally { setGenerating(false); }
  };

  const runInsights = async () => {
    if (!report) return;
    setInsightsLoading(true);
    try {
      const r = await axios.post(`${API}/km-benchmarking/reports/${report.report_id}/ai-insights`, {}, { headers });
      setReport(prev => ({ ...prev, ai_insights: r.data.ai_insights }));
      if (r.data.ai_insights?.error) {
        toast.error('Insights generation failed — try again.');
      } else {
        toast.success('AI insights ready');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate insights');
    } finally { setInsightsLoading(false); }
  };

  const downloadPdf = async () => {
    if (!report) return;
    try {
      const r = await axios.get(`${API}/km-benchmarking/reports/${report.report_id}/export.pdf`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `km-benchmark-${report.industry}-${report.company_size}-${report.created_at.slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF export failed');
    }
  };

  // ===== Email share =====
  const addShareEmail = (raw) => {
    const e = (raw || shareEmailInput).trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error('Invalid email address');
      return;
    }
    if (shareEmails.includes(e)) { setShareEmailInput(''); return; }
    setShareEmails([...shareEmails, e]);
    setShareEmailInput('');
  };
  const removeShareEmail = (e) => setShareEmails(shareEmails.filter(x => x !== e));
  const openShareDialog = () => {
    setShareEmails([]);
    setShareEmailInput('');
    setShareMessage('');
    setShareIncludePdf(true);
    setShareOpen(true);
  };
  const sendShare = async () => {
    // If the user typed an email but didn't hit Enter, commit it before sending.
    const pending = shareEmailInput.trim().toLowerCase();
    let finalEmails = [...shareEmails];
    if (pending) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(pending)) {
        toast.error(`"${pending}" is not a valid email`);
        return;
      }
      if (!finalEmails.includes(pending)) finalEmails.push(pending);
    }
    if (finalEmails.length === 0) {
      toast.error('Add at least one recipient email');
      return;
    }
    setSharing(true);
    try {
      const r = await axios.post(
        `${API}/km-benchmarking/reports/${report.report_id}/share`,
        { to_emails: finalEmails, message: shareMessage, include_pdf: shareIncludePdf },
        { headers },
      );
      const sent = r.data.sent_count || 0;
      const failed = r.data.failed_count || 0;
      if (failed > 0) {
        toast.warning(`Sent to ${sent} recipient${sent !== 1 ? 's' : ''}; ${failed} failed.`);
      } else {
        toast.success(`Report sent to ${sent} recipient${sent !== 1 ? 's' : ''}`);
      }
      setShareOpen(false);
      loadActivity();  // refresh the Share Activity card with the new row
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to share report');
    } finally { setSharing(false); }
  };

  const loadActivity = async () => {
    if (!report) return;
    setActivityLoading(true);
    try {
      const r = await axios.get(`${API}/km-benchmarking/reports/${report.report_id}/share-activity`, { headers });
      setActivity(r.data);
    } catch { /* silent */ } finally { setActivityLoading(false); }
  };

  // Compute unopened count from the current activity state so the button label is honest.
  // We dedupe by email — same recipient shared twice + never opened still counts once.
  const unopenedCount = (() => {
    if (!activity?.activity?.length) return 0;
    const opened = new Set();
    const sentOk = new Set();
    activity.activity.forEach(row => {
      const em = (row.email || '').toLowerCase();
      if (!em) return;
      if (row.send_status === 'sent') sentOk.add(em);
      if ((row.opened_count || 0) > 0) opened.add(em);
    });
    return [...sentOk].filter(e => !opened.has(e)).length;
  })();

  const nudgeUnopened = async () => {
    if (!report || unopenedCount === 0) return;
    if (!window.confirm(
      `Send a follow-up email to ${unopenedCount} recipient${unopenedCount === 1 ? '' : 's'} ` +
      `who haven't opened the report yet? They'll get a fresh tracking link with a "Just following up…" note.`
    )) return;
    setNudging(true);
    try {
      const r = await axios.post(
        `${API}/km-benchmarking/reports/${report.report_id}/nudge-unopened`,
        { include_pdf: true },
        { headers },
      );
      const sent = r.data.sent_count || 0;
      const failed = r.data.failed_count || 0;
      if (failed > 0) {
        toast.warning(`Nudged ${sent} recipient${sent !== 1 ? 's' : ''}; ${failed} failed.`);
      } else {
        toast.success(`Nudged ${sent} non-opener${sent !== 1 ? 's' : ''}`);
      }
      loadActivity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nudge failed');
    } finally { setNudging(false); }
  };

  // Auto-load activity whenever we switch to a new report.
  useEffect(() => {
    setActivity(null);
    setTrajectory(null);
    if (report?.report_id) {
      loadActivity();
      loadTrajectory();
    }
  }, [report?.report_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTrajectory = async () => {
    if (!report) return;
    try {
      const r = await axios.get(`${API}/km-benchmarking/trajectory`, {
        headers,
        params: {
          industry: report.industry,
          company_size: report.company_size,
          scope: report.scope,
          limit: 12,
        },
      });
      setTrajectory(r.data);
    } catch { /* silent */ }
  };

  // Radar data — 4 pillars, org vs industry P50 + P75
  const radarData = report ? [
    { pillar: 'Process',    org: report.org_scores.process,    p50: report.benchmark_snapshot.percentiles.process?.p50,    p75: report.benchmark_snapshot.percentiles.process?.p75 },
    { pillar: 'People',     org: report.org_scores.people,     p50: report.benchmark_snapshot.percentiles.people?.p50,     p75: report.benchmark_snapshot.percentiles.people?.p75 },
    { pillar: 'Technology', org: report.org_scores.technology, p50: report.benchmark_snapshot.percentiles.technology?.p50, p75: report.benchmark_snapshot.percentiles.technology?.p75 },
    { pillar: 'Overall',    org: report.org_scores.overall,    p50: report.benchmark_snapshot.percentiles.overall?.p50,    p75: report.benchmark_snapshot.percentiles.overall?.p75 },
  ] : [];

  return (
    <div data-testid="km-benchmarking-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="benchmarking-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />Back
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] flex items-center gap-2">
              <Target className="w-6 h-6 text-[#002FA7]" />KM Benchmarking
            </h1>
            <p className="text-sm text-[#4B5563] mt-0.5">
              Compare your organisation's KM maturity against industry peers. Identify gaps, optimise resource allocation, project performance impact.
            </p>
          </div>
        </div>
      </div>

      {/* Selector card */}
      <Card className="border-[#E5E7EB] mb-4">
        <CardContent className="p-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <Label className="text-xs text-[#4B5563]">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger data-testid="benchmarking-industry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.industries.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#4B5563]">Company size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger data-testid="benchmarking-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.sizes.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#4B5563]">Aggregation scope</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="benchmarking-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="my">My submissions only</SelectItem>
                  <SelectItem value="domain">My email domain (team/org)</SelectItem>
                  <SelectItem value="all">All submissions</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={runReport}
                disabled={generating}
                className="w-full bg-[#002FA7] hover:bg-[#00227A]"
                data-testid="benchmarking-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <TrendingUp className="w-4 h-4 mr-1.5" />}
                Generate report
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-2">
            Industry norms calibrated from APQC, KM Institute, and Gartner public maturity studies. Admins can edit values at any time.
          </p>
        </CardContent>
      </Card>

      {!report && !generating && (
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 text-[#E5E7EB] mx-auto mb-3" />
            <p className="text-sm text-[#9CA3AF]">
              Select your industry + size, then <strong>Generate report</strong> to see how you rank against peers.
            </p>
          </CardContent>
        </Card>
      )}

      {report && (
        <div className="space-y-4" data-testid="benchmarking-report">
          {/* Top KPIs */}
          <div className="grid gap-3 md:grid-cols-4">
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">Your Overall</p>
                <p className="text-3xl font-bold text-[#0A0A0A] mt-1" data-testid="benchmarking-org-overall">{report.org_scores.overall}</p>
                <p className="text-[11px] text-[#4B5563] mt-0.5">n={report.submissions_n} submissions</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">Industry P50</p>
                <p className="text-3xl font-bold text-[#002FA7] mt-1">{report.benchmark_snapshot.percentiles.overall?.p50}</p>
                <p className="text-[11px] text-[#4B5563] mt-0.5">Peer median</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">Your Percentile</p>
                <p className="text-3xl font-bold text-[#34C759] mt-1" data-testid="benchmarking-percentile">{report.percentile}<span className="text-lg text-[#9CA3AF]">th</span></p>
                <p className="text-[11px] text-[#4B5563] mt-0.5">vs industry cohort</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-3 flex flex-col gap-1.5">
                <Button
                  variant="outline" size="sm"
                  onClick={downloadPdf}
                  className="justify-start h-8 text-xs"
                  data-testid="benchmarking-pdf-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />Download PDF
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={openShareDialog}
                  className="justify-start h-8 text-xs"
                  data-testid="benchmarking-share-btn"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5" />Share via email
                </Button>
                <Button
                  size="sm"
                  onClick={runInsights}
                  disabled={insightsLoading}
                  className="justify-start h-8 text-xs bg-[#002FA7] hover:bg-[#00227A]"
                  data-testid="benchmarking-insights-btn"
                >
                  {insightsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  AI Insights
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Radar + Gap table side by side */}
          <div className="grid gap-3 lg:grid-cols-2">
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Pillar comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData} outerRadius={95}>
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis dataKey="pillar" tick={{ fill: '#4B5563', fontSize: 11 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#9CA3AF', fontSize: 9 }} />
                    <Radar name="Your org" dataKey="org" stroke="#002FA7" fill="#002FA7" fillOpacity={0.45} />
                    <Radar name="Peer P50"  dataKey="p50" stroke="#AF52DE" fill="#AF52DE" fillOpacity={0.15} />
                    <Radar name="Peer P75"  dataKey="p75" stroke="#34C759" fill="#34C759" fillOpacity={0.08} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <ReTooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Gap analysis</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-[#F3F4F6]" data-testid="benchmarking-gap-table">
                  {report.gap_analysis.map(g => {
                    const meta = SEVERITY_META[g.severity] || SEVERITY_META.minor;
                    return (
                      <div key={g.pillar} className="p-3" data-testid={`gap-row-${g.pillar}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-[#0A0A0A] capitalize">{g.pillar}</span>
                          <Badge className={`text-[10px] px-1.5 py-0 h-[16px] border ${meta.bg} ${meta.color} ${meta.border}`}>
                            <meta.Icon className="w-2.5 h-2.5 mr-0.5 inline" />{meta.label}
                          </Badge>
                          <span className="ml-auto text-[11px] text-[#4B5563]">
                            <strong className="text-[#0A0A0A]">{g.org}</strong>
                            <span className="mx-1 text-[#9CA3AF]">vs</span>
                            P50 {g.benchmark_p50}
                            <span className="mx-1 text-[#9CA3AF]">·</span>
                            P75 {g.benchmark_p75}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`text-[11px] font-semibold ${g.gap_pts > 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                            {g.gap_pts > 0 ? '−' : '+'}{Math.abs(g.gap_pts).toFixed(1)} pts
                          </span>
                          <span className="text-[11px] text-[#4B5563]">{g.impact_narrative}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Projected KPI uplift */}
          <Card className="border-[#E5E7EB]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#002FA7]" />Projected KPI uplift if you close the P50 gap
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="benchmarking-kpi-grid">
                {Object.entries(report.projected_kpi_uplift || {}).map(([k, v]) => {
                  const meta = KPI_LABELS[k];
                  if (!meta) return null;
                  const positive = v > 0;
                  return (
                    <div key={k} className="p-3 rounded border border-[#E5E7EB] bg-[#FAFAFA]">
                      <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{meta.label}</p>
                      <p className={`text-xl font-bold mt-0.5 ${positive ? 'text-[#34C759]' : 'text-[#FF9500]'}`}>
                        {positive ? '+' : ''}{v.toFixed(1)}
                        <span className="text-[11px] font-normal text-[#4B5563] ml-1">{meta.unit}</span>
                      </p>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-2">
                Projections derived from KM research curves (APQC / KM Institute composites). Delta represents what reaching the industry P50 baseline typically unlocks for peers of your size.
              </p>
            </CardContent>
          </Card>

          {/* Benchmark Trajectory — sparkline of org overall vs industry P50 over time.
              Each /report call upserts a daily snapshot; this card auto-loads the latest
              12 so execs can see at-a-glance whether the org is trending toward or
              away from the peer median. */}
          {trajectory && (
            <Card className="border-[#E5E7EB]" data-testid="benchmarking-trajectory-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#002FA7]" />
                    Trajectory
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-[16px]">
                      {trajectory.count} snapshot{trajectory.count === 1 ? '' : 's'}
                    </Badge>
                  </CardTitle>
                  {trajectory.delta_overall !== null && (
                    <Badge
                      className={`text-[10px] px-2 py-0.5 h-auto ${
                        trajectory.delta_overall > 0
                          ? 'bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30'
                          : trajectory.delta_overall < 0
                            ? 'bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30'
                            : 'bg-[#9CA3AF]/10 text-[#9CA3AF] border border-[#9CA3AF]/30'
                      }`}
                      data-testid="benchmarking-trajectory-delta"
                    >
                      {trajectory.delta_overall > 0 ? '▲' : trajectory.delta_overall < 0 ? '▼' : '—'}
                      &nbsp;{trajectory.delta_overall > 0 ? '+' : ''}{trajectory.delta_overall.toFixed(1)} pts since first snapshot
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-3">
                {trajectory.count < 2 ? (
                  <div className="p-8 text-center text-xs text-[#9CA3AF]">
                    Only one snapshot so far — generate the report again tomorrow to start your trajectory.
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart
                        data={trajectory.snapshots.map(s => ({
                          date: s.snapshot_date.slice(5),  // MM-DD
                          org: s.org_scores.overall,
                          p50: s.benchmark_p50.overall,
                        }))}
                        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#9CA3AF', fontSize: 10 }} axisLine={false} tickLine={false} width={32} />
                        <ReTooltip
                          contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                          formatter={(v, name) => [v, name === 'org' ? 'Your org' : 'Peer P50']}
                        />
                        {trajectory.snapshots.length > 0 && (
                          <ReferenceLine
                            y={trajectory.snapshots[0].benchmark_p50.overall}
                            stroke="#AF52DE"
                            strokeDasharray="4 4"
                            label={{ value: 'P50', position: 'right', fill: '#AF52DE', fontSize: 10 }}
                          />
                        )}
                        <Line type="monotone" dataKey="org" stroke="#002FA7" strokeWidth={2.5} dot={{ r: 3, fill: '#002FA7' }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="p50" stroke="#AF52DE" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-3 text-[10px] text-[#4B5563] mt-1 flex-wrap">
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-[#002FA7] rounded" />Your org</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-[#AF52DE] rounded" style={{ borderTop: '1.5px dashed #AF52DE', borderBottom: 'none', background: 'transparent' }} />Peer P50</span>
                      <span className="ml-auto text-[#9CA3AF]">
                        {trajectory.snapshots[0]?.snapshot_date} → {trajectory.snapshots[trajectory.snapshots.length - 1]?.snapshot_date}
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Insights */}
          {report.ai_insights && !report.ai_insights.error && (
            <Card className="border-[#002FA7]/30 bg-[#002FA7]/[0.02]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#002FA7]" />AI-Generated Insights
                  <Badge variant="outline" className="text-[9px] ml-1">GPT-5.2</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3" data-testid="benchmarking-ai-insights">
                {report.ai_insights.executive_summary && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-0.5">Executive Summary</p>
                    <p className="text-sm text-[#0A0A0A] leading-relaxed">{report.ai_insights.executive_summary}</p>
                  </div>
                )}
                {report.ai_insights.root_causes?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">Root Causes</p>
                    <ul className="space-y-1">
                      {report.ai_insights.root_causes.map((rc, i) => (
                        <li key={i} className="text-xs text-[#0A0A0A] flex gap-2">
                          <Badge variant="outline" className="text-[9px] px-1 py-0 h-[14px] capitalize">{rc.pillar}</Badge>
                          <span>{rc.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {report.ai_insights.recommendations?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">Recommendations</p>
                    <ul className="space-y-1.5">
                      {report.ai_insights.recommendations.map((r, i) => (
                        <li key={i} className="text-xs text-[#0A0A0A] flex gap-2">
                          <Badge className="text-[9px] px-1 py-0 h-[14px] capitalize bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/30">{r.pillar}</Badge>
                          <Badge variant="outline" className="text-[9px] px-1 py-0 h-[14px] capitalize">{r.effort} effort</Badge>
                          <span className="flex-1">{r.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {report.ai_insights.performance_impact_narrative && (
                  <div className="pt-2 border-t border-[#E5E7EB]">
                    <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-0.5">Performance Impact</p>
                    <p className="text-sm text-[#0A0A0A] leading-relaxed italic">{report.ai_insights.performance_impact_narrative}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {report.ai_insights?.error && (
            <Card className="border-[#FF3B30]/30 bg-[#FFF5F5]">
              <CardContent className="p-3 text-xs text-[#FF3B30]">
                Insights generation failed: {report.ai_insights.error}. Click AI Insights again to retry.
              </CardContent>
            </Card>
          )}

          {/* Share Activity — only renders once the report has been shared at least once.
              Per-recipient open tracking lets the author see "who opened" without
              relying on replies. */}
          {activity && activity.totals.recipients > 0 && (
            <Card className="border-[#E5E7EB]" data-testid="benchmarking-share-activity-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MailCheck className="w-4 h-4 text-[#002FA7]" />
                    Share activity
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-[16px]">
                      {activity.totals.opened}/{activity.totals.recipients} opened
                    </Badge>
                  </CardTitle>
                  <div className="flex items-center gap-1.5">
                    {unopenedCount > 0 && (
                      <Button
                        variant="outline" size="sm"
                        onClick={nudgeUnopened}
                        disabled={nudging}
                        className="h-7 text-[11px] border-[#FF9500]/40 text-[#FF9500] hover:bg-[#FF9500]/10"
                        data-testid="benchmarking-nudge-btn"
                      >
                        {nudging
                          ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          : <BellRing className="w-3 h-3 mr-1" />}
                        Nudge non-openers ({unopenedCount})
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="sm"
                      onClick={loadActivity}
                      disabled={activityLoading}
                      className="h-7 text-[11px]"
                      data-testid="benchmarking-activity-refresh-btn"
                    >
                      {activityLoading
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <RefreshCw className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-[#F3F4F6]" data-testid="benchmarking-activity-rows">
                  {activity.activity.map((row, i) => {
                    const sentFailed = row.send_status === 'failed';
                    const opened = row.opened_count > 0;
                    return (
                      <div key={i} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`activity-row-${row.email}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-[#0A0A0A]">{row.email}</span>
                            {sentFailed ? (
                              <Badge className="text-[9px] px-1 py-0 h-[14px] bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30">
                                Send failed
                              </Badge>
                            ) : (
                              <Badge className="text-[9px] px-1 py-0 h-[14px] bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30">
                                Sent
                              </Badge>
                            )}
                            {row.included_pdf && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-[14px]">PDF</Badge>
                            )}
                            {opened ? (
                              <Badge className="text-[9px] px-1 py-0 h-[14px] bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/30">
                                <Eye className="w-2.5 h-2.5 mr-0.5 inline" />
                                Opened {row.opened_count}×
                              </Badge>
                            ) : row.tracked && !sentFailed ? (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-[14px] text-[#9CA3AF]">
                                Not opened yet
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-[10px] text-[#9CA3AF] mt-0.5">
                            Sent {row.shared_at ? formatDistanceToNow(new Date(row.shared_at), { addSuffix: true }) : '—'}
                            {opened && row.first_opened_at && (
                              <> · First opened {formatDistanceToNow(new Date(row.first_opened_at), { addSuffix: true })}</>
                            )}
                            {opened && row.last_opened_at && row.last_opened_at !== row.first_opened_at && (
                              <> · Last opened {formatDistanceToNow(new Date(row.last_opened_at), { addSuffix: true })}</>
                            )}
                          </div>
                          {sentFailed && row.send_error && (
                            <div className="text-[10px] text-[#FF3B30] mt-0.5 line-clamp-1">{row.send_error}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="p-2 border-t border-[#F3F4F6] bg-[#FAFAFA] text-[10px] text-[#9CA3AF]">
                  Opens are tracked via the "View full report online" link in the email. PDF attachments are not tracked.
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Email Share Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md" data-testid="benchmarking-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#002FA7]" />
              Share benchmarking report
            </DialogTitle>
            <DialogDescription className="text-xs">
              Send the executive-ready report to stakeholders. Optional PDF attachment includes the
              full gap analysis, projected KPI uplift, and AI-generated recommendations.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#4B5563]">Recipient emails</Label>
              <div className="flex gap-1.5 mt-1">
                <Input
                  value={shareEmailInput}
                  onChange={(e) => setShareEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addShareEmail();
                    }
                  }}
                  placeholder="name@company.com — Enter to add"
                  className="text-xs"
                  data-testid="benchmarking-share-email-input"
                />
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => addShareEmail()}
                  data-testid="benchmarking-share-add-email-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              {shareEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="benchmarking-share-email-chips">
                  {shareEmails.map(e => (
                    <Badge
                      key={e}
                      variant="outline"
                      className="text-[10px] pl-2 pr-1 py-0.5 gap-1"
                    >
                      {e}
                      <button
                        type="button"
                        onClick={() => removeShareEmail(e)}
                        className="hover:bg-[#FF3B30]/10 rounded-full p-0.5"
                        aria-label={`Remove ${e}`}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-[#4B5563]">Message (optional)</Label>
              <Textarea
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                placeholder="Add a note for recipients, e.g. 'FYI — our quarterly peer comparison. Key gap is in People — see p.2.'"
                rows={3}
                maxLength={1000}
                className="text-xs resize-none mt-1"
                data-testid="benchmarking-share-message"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-[#4B5563] cursor-pointer">
              <input
                type="checkbox"
                checked={shareIncludePdf}
                onChange={(e) => setShareIncludePdf(e.target.checked)}
                className="rounded border-[#D1D5DB] text-[#002FA7] focus:ring-[#002FA7]"
                data-testid="benchmarking-share-include-pdf"
              />
              <span>Attach full PDF report (recommended)</span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline" size="sm"
              onClick={() => setShareOpen(false)}
              disabled={sharing}
              data-testid="benchmarking-share-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={sendShare}
              disabled={sharing}
              className="bg-[#002FA7] hover:bg-[#00227A]"
              data-testid="benchmarking-share-send-btn"
            >
              {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KMBenchmarkingPanel;
